/* The pieces all three scheduling cards render: the state chip and the fact grid.
 *
 * Shared because the two schedule surfaces show the same seven facts in the same order, and because
 * the state chip is the one element a reader uses to tell a decision (disabled) from a condition
 * (suspended) from an absence (not configured). Three copies of that distinction is three chances
 * to get it wrong.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  OUTCOME_TONE,
  formatInstant,
  coversEveryDay,
  weekdayKey,
  type Presence,
  type ScheduleOutcome,
} from '../model/presentation';

export interface StateChipProps {
  readonly presence: Presence;
}

/** Enabled / Disabled / Suspended until … / Waiting for the first sweep.
 *
 * **Four labels for four states, and `unconfigured` renders nothing.** A card with no schedule has
 * no state to report — the body says so in a sentence — and a chip reading "Not configured" beside
 * a heading that already says "Harvest schedule" would be the same fact twice.
 */
export function StateChip({ presence }: StateChipProps): ReactElement | null {
  const { t } = useTranslation();

  if (presence.kind === 'unconfigured') {
    return null;
  }
  if (presence.kind === 'suspended') {
    return (
      <span className="lq-sched__chip lq-sched--warn" title={presence.reason}>
        {t('scheduling.state.suspendedUntil', { until: presence.until })}
      </span>
    );
  }
  if (presence.kind === 'disabled') {
    return (
      <span className="lq-sched__chip lq-sched--neutral">
        {t('scheduling.state.disabled')}
      </span>
    );
  }
  return (
    <span className="lq-sched__chip lq-sched--ok">{t('scheduling.state.enabled')}</span>
  );
}

export interface ScheduleFactsProps {
  readonly localTime?: string | undefined;
  readonly timezone?: string | undefined;
  readonly cadence?: 'daily' | 'weekly' | undefined;
  readonly weekdays?: readonly number[] | undefined;
  readonly nextRunAt: string | null;
  readonly lastRunAt: string | null;
  readonly outcome: ScheduleOutcome | null;
  readonly enabled: boolean;
}

/** Time / Timezone / Cadence / Weekdays / Next run, and Last run + Outcome once there is one.
 *
 * **The timezone is never omitted and never abbreviated to an offset.** `02:00` alone is the
 * ambiguity this whole feature exists to remove, so the zone travels beside the time in its own
 * labelled cell — a reader must never have to remember which zone a schedule was agreed in.
 *
 * **Last run and Outcome appear together or not at all.** The API keeps them coupled — an outcome
 * with no instant is a state no firing produces — and rendering the pair only when there is one is
 * what stops a never-run schedule showing an empty Outcome cell that reads as a result.
 */
export function ScheduleFacts({
  localTime,
  timezone,
  cadence,
  weekdays,
  nextRunAt,
  lastRunAt,
  outcome,
  enabled,
}: ScheduleFactsProps): ReactElement {
  const { t } = useTranslation();

  const weekdayLabel = coversEveryDay(weekdays)
    ? t('scheduling.weekdays.every')
    : (weekdays ?? []).map((day) => t(weekdayKey(day))).join(', ');

  /* Three readings, and they are three different facts. A disabled schedule has no next run
     *because it is off* — the API sends null and rendering a future time would promise a run that
     will not happen. An enabled one with no cached occurrence is waiting for the sweep. Collapsing
     either into a dash would make "off" and "waiting" the same sentence. */
  /* Formatted in the schedule's own zone, and **falling back to the raw instant rather than to a
     dash** — a dash here reads as "nothing is coming", which is a claim a formatting failure has
     no grounds to make. */
  const nextRun =
    nextRunAt === null
      ? enabled
        ? t('scheduling.nextRun.waiting')
        : t('scheduling.nextRun.none')
      : (formatInstant(nextRunAt, timezone) ?? nextRunAt);

  return (
    <>
      <div className="lq-sched__facts">
        <div>
          <div className="lq-sched__label">{t('scheduling.fact.time')}</div>
          <div className="lq-sched__value lq-mono">{localTime ?? '—'}</div>
        </div>
        <div>
          <div className="lq-sched__label">{t('scheduling.fact.timezone')}</div>
          <div className="lq-sched__value lq-mono">{timezone ?? '—'}</div>
        </div>
        <div>
          <div className="lq-sched__label">{t('scheduling.fact.cadence')}</div>
          <div className="lq-sched__value">
            {cadence === undefined ? '—' : t(`scheduling.cadence.${cadence}`)}
          </div>
        </div>
        <div>
          <div className="lq-sched__label">{t('scheduling.fact.weekdays')}</div>
          <div className="lq-sched__value">{weekdayLabel}</div>
        </div>
        <div>
          <div className="lq-sched__label">{t('scheduling.fact.nextRun')}</div>
          <div className="lq-sched__value lq-mono">{nextRun}</div>
        </div>
        {lastRunAt !== null && outcome !== null ? (
          <>
            <div>
              <div className="lq-sched__label">{t('scheduling.fact.lastRun')}</div>
              <div className="lq-sched__value lq-mono">
                {formatInstant(lastRunAt, timezone) ?? lastRunAt}
              </div>
            </div>
            <div>
              <div className="lq-sched__label">{t('scheduling.fact.outcome')}</div>
              <div className="lq-sched__value">
                <span className={`lq-sched__outcome lq-sched--${OUTCOME_TONE[outcome]}`}>
                  {t(`scheduling.outcome.${outcome}`)}
                </span>
              </div>
            </div>
          </>
        ) : null}
      </div>
      {/* **The resolution of the promise above, and it is the difference between a working
          schedule and one a reader calls broken.** Occurrences are claimed by a sweep on a fixed
          interval, so a schedule set to 15:55 is dispatched at the first tick at or after 15:55 —
          the platform's own schedule description states it in these words: "bounds how *late* an
          occurrence can be, not how often a harvest happens". Rendering the instant alone reads as
          an exact promise, and a reader watching the minute pass concludes nothing fired. Observed
          doing exactly that.

          **No number here, deliberately.** The interval is a backend constant with no field on
          this payload, so a hardcoded "15 minutes" would be a second copy that silently stops
          matching the day somebody tunes it — this platform's most-recorded defect. The exact
          bound is already on the Platform screen, in the scheduler capability's own detail. */}
      {nextRunAt !== null ? (
        <p className="lq-sched__note">{t('scheduling.nextRun.resolution')}</p>
      ) : null}
    </>
  );
}
